import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import { currentSportUnreadNotificationsCountFragment } from '#src/contexts/currentUser/queries.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { idFromObject } from '#src/gql/idFromObject.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import {
  importantNotificationCategories,
  notificationCategories,
} from '#src/lib/notifications.ts';

import type {
  MarkNotificationsAsReadMutation,
  MarkNotificationsAsReadMutationVariables,
} from './__generated__/useMarkNotificationsAsRead.graphql.ts';

const MARK_NOTIFICATIONS_AS_READ_MUTATION = gql`
  mutation MarkNotificationsAsReadMutation(
    $input: markNotificationsAsReadInput!
    $sport: Sport!
    $notificationCategories: [NotificationCategoryInput!]
    $importantNotificationCategories: [NotificationCategoryInput!]
  ) {
    markNotificationsAsRead(input: $input) {
      currentUser {
        slug
        ...CurrentUserProvider_currentSportUnreadNotificationsCount
      }
      notifications {
        id
        read
      }
      errors {
        path
        message
        code
      }
    }
  }
  ${currentSportUnreadNotificationsCountFragment}
` as TypedDocumentNode<
  MarkNotificationsAsReadMutation,
  MarkNotificationsAsReadMutationVariables
>;

export const useMarkNotificationsAsRead = () => {
  const [markNotificationsAsReadMutation] = useMutation(
    MARK_NOTIFICATIONS_AS_READ_MUTATION
  );
  const { sport } = useSportContext();

  const markNotificationsAsRead = useCallback(
    async (notifications?: { id: string }[]) => {
      const notificationIds = notifications
        ?.map(({ id }) => idFromObject(id))
        .filter(Boolean);

      await markNotificationsAsReadMutation({
        variables: {
          input: {
            notificationIds,
          },
          sport,
          notificationCategories,
          importantNotificationCategories,
        },
      });
    },
    [markNotificationsAsReadMutation, sport]
  );
  return markNotificationsAsRead;
};
